// PS-style trophy system — persisted in localStorage, toasts via subscribe().
import { useEffect, useState } from "react";
import { sfx } from "@/lib/sound";

export const TROPHIES = [
  { id: "system-online", tier: "bronze", name: "System Online", desc: "Boot the IDEVRA console" },
  { id: "explorer", tier: "silver", name: "Sector Explorer", desc: "Visit every sector of the console" },
  { id: "collector", tier: "gold", name: "IP Collector", desc: "Open every venture dossier" },
  { id: "keyboard-operator", tier: "bronze", name: "Mech Operator", desc: "Issue 15 keyboard commands" },
  { id: "deep-dive", tier: "bronze", name: "Deep Dive", desc: "Launch a venture's external site" },
  { id: "premiere", tier: "bronze", name: "Premiere Night", desc: "Watch the Toothrocket brand film" },
  { id: "konami", tier: "gold", name: "Konami Operator", desc: "↑↑↓↓←→←→ B A — the legendary code", secret: true },
  { id: "archivist", tier: "silver", name: "Vault Archivist", desc: "Study every patent & trademark in the vault", secret: true },
  { id: "platinum", tier: "platinum", name: "IDEVRA Platinum", desc: "Unlock every trophy" },
];

const KEY = "idevra_trophies";
const API = process.env.REACT_APP_BACKEND_URL;

class TrophyManager {
  constructor() {
    this.unlocked = new Set(JSON.parse(localStorage.getItem(KEY) || "[]"));
    this.listeners = new Set();
  }

  has(id) {
    return this.unlocked.has(id);
  }

  count() {
    return this.unlocked.size;
  }

  subscribe(fn) {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  unlock(id) {
    if (this.unlocked.has(id)) return;
    this.unlocked.add(id);
    localStorage.setItem(KEY, JSON.stringify([...this.unlocked]));
    const t = TROPHIES.find((x) => x.id === id);
    sfx.trophy();
    this.listeners.forEach((fn) => fn(t));
    fetch(`${API}/api/trophies/event`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ trophy_id: id }),
    }).catch(() => {});
    if (
      id !== "platinum" &&
      TROPHIES.every((x) => x.id === "platinum" || this.unlocked.has(x.id))
    ) {
      setTimeout(() => this.unlock("platinum"), 1400);
    }
  }
}

export const trophies = new TrophyManager();

export function trackProgress(storeKey, value, target, trophyId) {
  const set = new Set(JSON.parse(localStorage.getItem(storeKey) || "[]"));
  if (!set.has(value)) {
    set.add(value);
    localStorage.setItem(storeKey, JSON.stringify([...set]));
  }
  if (set.size >= target) trophies.unlock(trophyId);
}

export function bumpKeys() {
  const n = Number(localStorage.getItem("idevra_keys") || "0") + 1;
  localStorage.setItem("idevra_keys", String(n));
  if (n >= 15) trophies.unlock("keyboard-operator");
}

export function useTrophyCount() {
  const [, force] = useState(0);
  useEffect(() => trophies.subscribe(() => force((v) => v + 1)), []);
  return trophies.count();
}

